import { useEffect, useRef, useState } from "react";
import { ChallengeShell, type ChallengeProps } from "./shared";
import { speak } from "../../audio";

/**
 * Spelling challenge — the question shows ONLY the picture + a speaker (no
 * written word). Below sit one blank box per letter and a shuffled bank of
 * letter tiles. Tapping a tile makes the letter FLY into the next empty box;
 * tapping a filled box sends the letter back. When every box is filled
 * correctly the word is spoken again and the gate releases. A wrong spelling
 * shakes, empties the boxes, and lets the student try again (no heart lost,
 * matching the other challenges' retry-until-right flow).
 */

interface Tile {
  id: number;
  ch: string;
}
interface Placed {
  tileId: number;
  ch: string;
  /** True while the fly-in clone is mid-flight (real letter stays hidden). */
  flying: boolean;
}

const MAX_TILES = 8;

function buildBank(target: string): Tile[] {
  const letters = target.split("");
  // A couple of decoy letters (not already in the word) so the bank isn't a
  // dead giveaway — capped so the row never overflows on a tablet.
  const pool = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").filter((c) => !target.includes(c));
  const extra = target.length >= 3 ? Math.min(2, MAX_TILES - letters.length) : 0;
  for (let i = 0; i < extra && pool.length; i++) {
    letters.push(pool.splice(Math.floor(Math.random() * pool.length), 1)[0]!);
  }
  // Shuffle, then tag with stable ids.
  for (let i = letters.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [letters[i], letters[j]] = [letters[j]!, letters[i]!];
  }
  return letters.map((ch, id) => ({ id, ch }));
}

/** position:fixed clone that animates from the tapped tile to the target box. */
function flyLetter(fromEl: HTMLElement, toEl: HTMLElement, ch: string, onDone: () => void) {
  const a = fromEl.getBoundingClientRect();
  const b = toEl.getBoundingClientRect();
  const clone = document.createElement("div");
  clone.className = "spell-fly";
  clone.textContent = ch;
  clone.style.left = `${a.left}px`;
  clone.style.top = `${a.top}px`;
  clone.style.width = `${a.width}px`;
  clone.style.height = `${a.height}px`;
  document.body.appendChild(clone);
  void clone.offsetWidth; // force layout so the transition runs
  const dx = b.left - a.left + (b.width - a.width) / 2;
  const dy = b.top - a.top + (b.height - a.height) / 2;
  clone.style.transform = `translate(${dx}px, ${dy}px) scale(${b.width / a.width})`;
  let done = false;
  const cleanup = () => {
    if (done) return;
    done = true;
    clone.remove();
    onDone();
  };
  clone.addEventListener("transitionend", cleanup, { once: true });
  window.setTimeout(cleanup, 360); // safety net if transitionend is missed
}

export function SpellWord({ word, onAnswer }: ChallengeProps) {
  const target = word.english.toUpperCase().replace(/[^A-Z]/g, "");

  const bankRef = useRef<{ key: string; tiles: Tile[] } | null>(null);
  if (!bankRef.current || bankRef.current.key !== word.id) {
    bankRef.current = { key: word.id, tiles: buildBank(target) };
  }
  const bank = bankRef.current.tiles;

  const [placed, setPlaced] = useState<(Placed | null)[]>(() => target.split("").map(() => null));
  const [status, setStatus] = useState<"idle" | "correct" | "wrong">("idle");
  const tileEls = useRef(new Map<number, HTMLButtonElement>());
  const slotEls = useRef<(HTMLDivElement | null)[]>([]);
  const timers = useRef<number[]>([]);
  const later = (fn: () => void, ms: number) => { timers.current.push(window.setTimeout(fn, ms)); };
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const consumed = new Set(placed.filter(Boolean).map((p) => p!.tileId));
  const anyFlying = placed.some((p) => p?.flying);

  // When every box is filled (and nothing's still flying), grade it.
  useEffect(() => {
    if (status !== "idle") return;
    if (placed.some((p) => p == null || p.flying)) return;
    const guess = placed.map((p) => p!.ch).join("");
    if (guess === target) {
      setStatus("correct");
      speak(word.english, { audioUrl: word.audioUrl, lang: "en-US" });
      later(() => onAnswer(true), 1100);
    } else {
      setStatus("wrong");
      later(() => {
        setPlaced(target.split("").map(() => null));
        setStatus("idle");
      }, 850);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placed, status]);

  const tapTile = (tile: Tile) => {
    if (status !== "idle" || anyFlying || consumed.has(tile.id)) return;
    const idx = placed.findIndex((p) => p == null);
    if (idx < 0) return;
    const tileEl = tileEls.current.get(tile.id);
    const slotEl = slotEls.current[idx];
    setPlaced((prev) => prev.map((p, i) => (i === idx ? { tileId: tile.id, ch: tile.ch, flying: true } : p)));
    if (tileEl && slotEl) {
      flyLetter(tileEl, slotEl, tile.ch, () => {
        setPlaced((prev) => prev.map((p, i) => (i === idx && p ? { ...p, flying: false } : p)));
      });
    } else {
      setPlaced((prev) => prev.map((p, i) => (i === idx && p ? { ...p, flying: false } : p)));
    }
  };

  const tapSlot = (idx: number) => {
    if (status !== "idle" || anyFlying) return;
    if (!placed[idx]) return;
    setPlaced((prev) => prev.map((p, i) => (i === idx ? null : p)));
  };

  return (
    <ChallengeShell
      tag="Spell"
      prompt="Tap the letters to spell the word"
      imageUrl={word.imageUrl}
      speakerText={word.english}
      speakerAudioUrl={word.audioUrl}
    >
      <div className={`spell-boxes ${status}`}>
        {placed.map((p, i) => (
          <div
            key={i}
            ref={(el) => { slotEls.current[i] = el; }}
            className={`spell-box ${p && !p.flying ? "filled" : ""}`}
            onClick={() => tapSlot(i)}
          >
            {p && !p.flying ? <span className="spell-box-letter">{p.ch}</span> : null}
          </div>
        ))}
      </div>

      <div className="spell-bank">
        {bank.map((tile) => (
          <button
            key={tile.id}
            ref={(el) => { if (el) tileEls.current.set(tile.id, el); else tileEls.current.delete(tile.id); }}
            className="spell-tile"
            onClick={() => tapTile(tile)}
            disabled={consumed.has(tile.id) || status !== "idle"}
            style={{ visibility: consumed.has(tile.id) ? "hidden" : "visible" }}
          >
            {tile.ch}
          </button>
        ))}
      </div>
    </ChallengeShell>
  );
}
